import { Calendar } from 'lucide-react';
import { type InputHTMLAttributes, forwardRef, useId } from 'react';
import styles from './DateInput.module.css';

/**
 * @deprecated F19 — нативный browser date picker работает непредсказуемо на
 * Chrome / Firefox / Safari / mobile (сбрасывает значение, невидимый trigger
 * на некоторых темах). Используйте будущий `DatePicker` / `DateTimePicker` /
 * `DateRangePicker` (F2, react-day-picker). Оставлено для transition period —
 * следующий major cleanup удалит export.
 *
 * Новые места не должны импортировать `DateInput`.
 */

export type DateInputMode = 'date' | 'month' | 'datetime-local' | 'time';

export type DateInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> & {
  label: string;
  mode?: DateInputMode;
  hint?: string;
  error?: string | undefined;
};

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(function DateInput(
  { label, mode = 'date', hint, error, id, className, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={[styles.field, className ?? ''].filter(Boolean).join(' ')}>
      <label htmlFor={inputId} className={styles.label}>
        {label}
      </label>
      <div
        className={[
          styles.wrap,
          error ? styles.wrapError : '',
          rest.disabled ? styles.wrapDisabled : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <span className={styles.icon} aria-hidden="true">
          <Calendar size={14} />
        </span>
        <input
          ref={ref}
          id={inputId}
          type={mode}
          className={styles.input}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={describedBy}
          {...rest}
        />
      </div>
      {hint && !error && (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
});
